import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];

export default function EstimateDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: e, isLoading } = useQuery({
    queryKey: ["admin-estimate", id],
    queryFn: async () => (await api.get(`/admin/estimates/${id}`)).data,
  });

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/estimates/${id}`, { status });
      qc.setQueryData(["admin-estimate", id], { ...e, status });
      qc.invalidateQueries({ queryKey: ["admin-estimates"] });
      qc.invalidateQueries({ queryKey: ["admin-analytics"] });
      toast.success(`Status updated to "${status}"`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  if (isLoading) return <p className="text-slateblue text-sm">Loading…</p>;
  if (!e) return <p className="text-slateblue text-sm">Estimate not found.</p>;

  const digits = (e.phone || "").replace(/\D/g, "");
  const waPhone = digits.length === 10 ? `1${digits}` : digits;
  const waHref = `https://wa.me/${waPhone}?text=${encodeURIComponent(`Hi ${e.name}, this is VIALFA following up on your ${e.project_type} estimate request.`)}`;

  const rows = [
    ["Project type", e.project_type], ["Rooms / areas", e.rooms], ["Scope", e.scope],
    ["Timeline", e.timeline], ["Budget", e.budget], ["Name", e.name], ["Email", e.email],
    ["Phone", e.phone], ["Address", e.address], ["ZIP", e.zip_code], ["Language", e.lang],
    ["Submitted", new Date(e.created_at).toLocaleString()],
  ];

  return (
    <div data-testid="admin-estimate-detail">
      <Link to="/admin/estimates" data-testid="back-to-estimates" className="inline-flex items-center gap-2 text-sm font-bold text-slateblue hover:text-orange mb-6 transition-colors duration-200">
        <ArrowLeft size={16} /> Back to inbox
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-heading font-bold text-2xl tracking-tight">{e.name} — {e.project_type}</h1>
        <div className="flex flex-wrap gap-2">
          <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="action-whatsapp"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 hover:opacity-90 transition-opacity duration-200">
            <MessageCircle size={14} /> WhatsApp
          </a>
          <a href={`mailto:${e.email}`} data-testid="action-email"
            className="inline-flex items-center gap-2 bg-navy text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 hover:bg-navy-800 transition-colors duration-200">
            <Mail size={14} /> Email
          </a>
          <a href={`tel:${digits}`} data-testid="action-call"
            className="inline-flex items-center gap-2 border border-sandline bg-white text-navy text-xs font-bold uppercase tracking-widest px-4 py-2.5 hover:border-orange transition-colors duration-200">
            <Phone size={14} /> Call
          </a>
          {e.status === "new" && (
            <button onClick={() => setStatus("contacted")} data-testid="action-mark-contacted"
              className="inline-flex items-center gap-2 bg-orange text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 hover:bg-orange-bright transition-colors duration-200">
              Mark Contacted
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-sandline">
          <div className="px-6 py-4 border-b border-sandline">
            <h2 className="font-heading font-bold tracking-tight">Submission Details</h2>
          </div>
          <dl className="divide-y divide-offwhite-dark">
            {rows.map(([k, v]) => (
              <div key={k} className="px-6 py-3.5 grid grid-cols-3 gap-4 text-sm">
                <dt className="text-slateblue font-semibold">{k}</dt>
                <dd className="col-span-2 whitespace-pre-line">{v || "—"}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-sandline p-6" data-testid="status-panel">
            <h2 className="font-heading font-bold tracking-tight mb-4">Pipeline Status</h2>
            <select
              value={e.status}
              onChange={(ev) => setStatus(ev.target.value)}
              data-testid="status-select"
              className="w-full border border-sandline bg-offwhite px-3 py-2.5 text-sm font-semibold focus:outline-none focus:border-orange"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </select>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <span key={s} className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${e.status === s ? "bg-orange text-white" : "bg-offwhite-dark text-slateblue"}`}>{s}</span>
              ))}
            </div>
          </div>

          <div className="bg-white border border-sandline p-6" data-testid="photos-panel">
            <h2 className="font-heading font-bold tracking-tight mb-4">Photos ({(e.photos || []).length})</h2>
            {(e.photos || []).length === 0 ? (
              <p className="text-sm text-slateblue">No photos attached.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {e.photos.map((p, i) => (
                  <a key={i} href={fileUrl(p)} target="_blank" rel="noopener noreferrer" data-testid={`photo-${i}`}>
                    <img src={fileUrl(p)} alt={`Upload ${i + 1}`} className="w-full aspect-square object-cover border border-sandline hover:border-orange transition-colors duration-200" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
